import CommonCss from "nlib/common/common.module.scss";

import * as Icons from "@phosphor-icons/react";
import { checkIsBusinessUser } from "selectors/user";
import { getContactsData } from "selectors/contacts";
import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import AuBusinessNumbers from "utils/AuBusinessNumbers";
import Constants from "const/Constants";
import ContactsActions from "actions/ContactsActions";
import FieldContainer from "../FieldContainer";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Texts from "utils/Texts";
import classNames from "classnames";
import useDocumentContext from "hooks/useDocumentContext";
import useShowCommonModal from "hooks/useShowCommonModal";

const STATES = {
  WARNING: "warning",
  NEGATIVE: "negative",
  POSITIVE: "positive"
};

const normalizeVatId = (vatId) => {
  return vatId && `${vatId}`.replace(/\D/g, "");
};

const VatIdField = (props) => {
  const id = "vatId";

  const timeoutRef = useRef(null);

  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const contactsData = useSelector(getContactsData);

  const businessUser = useSelector(checkIsBusinessUser);

  const mountedRef = useRef(true);

  const {
    settings: { allowClientPayeeSelection } = {}
  } = useSelector(getSelectedBusinessData);

  const showCommonModal = useShowCommonModal();

  const {
    documentState: { [id]: value = "", abnStatusFetching, address },
    setDocumentState
  } = useDocumentContext();

  const [abnData, setAbnData] = useState(null);

  const { text: tooltipText, icon: titleIcon, state } = useMemo(() => {
    if (!value || !value.trim()) return {};
    if (!AuBusinessNumbers.validate(value)) {
      return {
        text: uiTexts.abnInvalid,
        icon: <Icons.WarningCircle className={CommonCss.negativeText} />,
        state: STATES.NEGATIVE
      };
    }
    if (abnStatusFetching) {
      return {
        text: uiTexts.abnFetching,
        icon: <Icons.Spinner className={classNames(CommonCss.rotate, CommonCss.highlightText)} />
      };
    }
    if (!abnData) return {};

    const { abnRegistered, gstRegistered, abnActive } = abnData;

    if (abnRegistered) {
      if (abnActive) {
        if (gstRegistered) {
          return {
            text: uiTexts.abnValidGstValid,
            icon: <Icons.CheckCircle className={CommonCss.positiveText} />,
            state: STATES.POSITIVE
          };
        }

        return {
          text: uiTexts.abnValidNotGstValid,
          icon: <Icons.CheckCircle className={CommonCss.positiveText} />,
          state: STATES.POSITIVE
        };
      }

      return {
        text: uiTexts.abnCancelled,
        icon: <Icons.WarningCircle className={CommonCss.warningText} />,
        state: STATES.WARNING
      };
    }

    return {
      text: uiTexts.abnNoRecord,
      icon: <Icons.WarningCircle className={CommonCss.warningText} />,
      state: STATES.WARNING
    };
  }, [abnStatusFetching, value, abnData, uiTexts]);

  const checkContactWithSameVatId = useCallback(async(searchValue, currentVendorName) => {
    const cleared = normalizeVatId(searchValue);

    if (!cleared) return;

    const foundContact = contactsData.find(({ vatId }) => normalizeVatId(vatId) === cleared);

    if (foundContact && foundContact.name !== currentVendorName) {
      const modalResult = await showCommonModal({
        text: Texts.replaceVars(
          messages.abnAlreadyUsedByVendor,
          { vendorName: foundContact.name }
        ),
        headerText: uiTexts.warning,
        confirm: true,
        okButtonText: uiTexts.changeVendor,
        cancelButtonText: uiTexts.clearAbnField
      });

      if (modalResult) {
        setDocumentState({ address: foundContact });
      } else {
        setDocumentState({ [id]: "" });
      }
    }
  }, [contactsData, setDocumentState, showCommonModal, uiTexts, messages]);

  const fetchAbnData = useCallback(async(abn) => {
    setDocumentState({ abnStatusFetching: true });

    const result = await dispatch(ContactsActions.fetchAbnData(abn));

    if (!mountedRef.current) return;
    setAbnData({ abn, ...(result || {}) });
    setDocumentState({ abnStatusFetching: false });
  }, [dispatch, setDocumentState]);

  const handleChange = useCallback((val) => {
    val = val.replace(/\s{2,}/g, " ").replace(/[^0-9 ]/g, "");

    const valid = AuBusinessNumbers.validate(val);

    setDocumentState({ [id]: valid ? AuBusinessNumbers.format(val) : val });
    timeoutRef.current = setTimeout(() => {
      const cleared = normalizeVatId(val);

      checkContactWithSameVatId(cleared, address?.name);
      if (abnData?.abn === cleared) return;
      if (AuBusinessNumbers.validate(cleared)) {
        fetchAbnData(cleared);
      } else {
        setAbnData(null);
      }
    }, Constants.TEXT_FIELD_DEBOUNCE_TIMEOUT);
  }, [abnData?.abn, fetchAbnData, setDocumentState, address?.name, checkContactWithSameVatId]);

  const handleBlur = useCallback((event) => {
    const cleared = event.target.value.replace(/\s{2,}/g, " ").replace(/[^0-9 ]/g, "").trim();

    setDocumentState({
      [id]: AuBusinessNumbers.validate(cleared)
        ? AuBusinessNumbers.format(cleared)
        : cleared
    });
  }, [setDocumentState]);

  useEffect(() => {
    if (AuBusinessNumbers.validate(value)) fetchAbnData(value);

    return () => {
      mountedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setDocumentState({ abnActive: !!abnData?.abnActive });
  }, [abnData?.abnActive, setDocumentState]);

  return (
    <FieldContainer
      id={id}
      label={uiTexts.abn}
      value={value}
      disabled={abnStatusFetching || (businessUser && !allowClientPayeeSelection)}
      titleIcon={titleIcon}
      data-tooltip={tooltipText}
      data-tooltip-style={state}
      placeholder={uiTexts.enterAbn}
      displayValue={AuBusinessNumbers.format(value)}
      onChange={handleChange}
      onBlur={handleBlur}
      {...props} />
  );
};

export default React.memo(VatIdField);
